import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild,} from "@angular/core";
import {
  NgbDatepicker,
  NgbDatepickerConfig,
  NgbDateStruct,
  NgbPopover,
  NgbPopoverConfig,
  NgbTimeStruct
} from "@ng-bootstrap/ng-bootstrap";
import {ControlValueAccessor, NgControl} from "@angular/forms";
import {DateTimeModel} from "./date-time.model";
import {noop} from "rxjs";
import {UtilsService} from "@service/utils/utils.service";

@Component({
  selector: "app-date-time-picker",
  templateUrl: "./date-time-picker.component.html",
  styleUrls: ["./date-time-picker.component.scss"]
})
export class DateTimePickerComponent
  implements ControlValueAccessor, OnInit, AfterViewInit {
  @Input()
  dateString: string;
  @Input()
  inputDatetimeFormat = "dd/MM/yyyy HH:mm";
  @Input()
  hourStep = 1;
  @Input()
  minuteStep = 60;
  @Input()
  secondStep = 30;
  @Input()
  seconds = true;
  @Input()
  disabled = false;
  @Input()
  disableTimePicker = false;
  @Input()
  invalid: any = false;

  public showTimePickerToggle = false;

  @Output() dateSelected: EventEmitter<DateTimeModel> = new EventEmitter();
  @Input() datetime: DateTimeModel = new DateTimeModel();
  @Input() hour: number = 0;
  public firstTimeAssign = true;

  @ViewChild(NgbPopover, {static: true})
  public popover: NgbPopover;

  public onTouched: () => void = noop;
  public onChange: (_: any) => void = noop;

  public ngControl: NgControl;

  constructor(public config: NgbPopoverConfig, private datepickerConfig: NgbDatepickerConfig, private utils: UtilsService) {
    config.autoClose = "outside";
    config.placement = "auto";
    const current = new Date();
    datepickerConfig.minDate = {year: current.getFullYear(), month: current.getMonth() + 1, day: current.getDate()};
    datepickerConfig.outsideDays = 'hidden';
  }

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    if (this.datetime) {
      this.setDateStringModel();
    }
    this.popover.hidden.subscribe($event => {
      this.showTimePickerToggle = false;
    });
  }

  writeValue(newModel: string) {
    if (newModel) {
      this.datetime = Object.assign(
        this.datetime,
        DateTimeModel.fromLocalString(newModel)
      );
      this.dateString = newModel;
      this.setDateStringModel();
    } else {
      this.datetime = new DateTimeModel();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  toggleDateTimeState($event) {
    this.showTimePickerToggle = !this.showTimePickerToggle;
    $event.stopPropagation();
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInputChange($event: any) {
    const value = $event.target.value;
    const dt = DateTimeModel.fromLocalString(value);

    if (dt) {
      this.datetime = dt;
      this.setDateStringModel();
    } else if (value.trim() === "") {
      this.datetime = new DateTimeModel();
      this.dateString = "";
      this.onChange(this.dateString);
    } else {
      this.onChange(value);
    }
  }

  onDateChange($event: string | NgbDateStruct, dp: NgbDatepicker) {
    const date = new DateTimeModel($event);

    if (!date) {
      this.dateString = this.dateString;
      return;
    }

    if (!this.datetime) {
      this.datetime = date;
    }


    if (this.datetime instanceof String || typeof this.datetime === 'string') {
      this.datetime = this.utils.splitDateStringForDateTimePicker(this.datetime, this.hour);
    }


    this.datetime.year = date.year;
    this.datetime.month = date.month;
    this.datetime.day = date.day;

    const adjustedDate = new Date(this.datetime.toString());
    if (this.datetime.timeZoneOffset !== adjustedDate.getTimezoneOffset()) {
      this.datetime.timeZoneOffset = adjustedDate.getTimezoneOffset();
    }

    this.setDateStringModel();
  }

  onTimeChange(event: NgbTimeStruct) {
    this.datetime.hour = event.hour;
    this.datetime.minute = event.minute;
    this.datetime.second = event.second;

    this.setDateStringModel();
  }

  setDateStringModel() {
    if (this.datetime instanceof String || typeof this.datetime === 'string') {
      this.datetime = this.utils.splitDateStringForDateTimePicker(this.datetime, this.hour);
    }

    this.dateString = this.datetime.toString();

    if (!this.firstTimeAssign) {
      this.onChange(this.dateString);
    } else {
      if (this.dateString !== null) {
        this.firstTimeAssign = false;
      }
    }
    this.dateSelected.emit(this.datetime);
  }

  checkTimeDisabled() {
    if (this.datetime instanceof String || typeof this.datetime === 'string') {
      this.datetime = this.utils.splitDateStringForDateTimePicker(this.datetime, this.hour);
    }
    return !this.datetime?.year || this.disableTimePicker;
  }

  inputBlur($event) {
    this.onTouched();
  }

  ngOnDestroy() {
    this.datepickerConfig.minDate = null;
    this.datepickerConfig.outsideDays = 'visible';
  }
}
